import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiService } from 'services/ApiService';
import type {
  BillingAccount,
  SetMonetizationModeRequest,
  StartOnboardingRequest,
  SubscriptionPlan,
} from 'models/billing/BillingAccount';
import { MonetizationMode } from 'models/billing/MonetizationMode';
import { OnboardingState } from 'models/billing/OnboardingState';
import { SubscriptionState } from 'models/billing/SubscriptionState';

export interface ArtistBillingSettingsProps {
  artistId: string;
}

// ── Onboarding helpers ────────────────────────────────────────────────────

function onboardingStateLabel(state: OnboardingState): string {
  switch (state) {
    case OnboardingState.NotStarted: return 'Not started';
    case OnboardingState.Pending: return 'Setup in progress';
    case OnboardingState.ChargesOnly: return 'Charges enabled';
    case OnboardingState.Complete: return 'Complete';
    case OnboardingState.Restricted: return 'Restricted';
    default: return state;
  }
}

function onboardingBadgeClass(state: OnboardingState): string {
  switch (state) {
    case OnboardingState.Complete:
      return 'bg-green-100 text-green-700 border-green-300';
    case OnboardingState.ChargesOnly:
      return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    case OnboardingState.Pending:
      return 'bg-blue-100 text-blue-700 border-blue-300';
    case OnboardingState.Restricted:
      return 'bg-red-100 text-red-700 border-red-300';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-300';
  }
}

// ── Subscription helpers ──────────────────────────────────────────────────

function subscriptionStateLabel(state: SubscriptionState): string {
  switch (state) {
    case SubscriptionState.Active: return 'Active';
    case SubscriptionState.PastDue: return 'Past due';
    case SubscriptionState.Incomplete: return 'Incomplete';
    case SubscriptionState.IncompleteExpired: return 'Expired';
    case SubscriptionState.Canceled: return 'Canceled';
    case SubscriptionState.Trialing: return 'Trial';
    case SubscriptionState.PendingCancellation: return 'Cancels at period end';
    default: return 'None';
  }
}

function subscriptionBadgeClass(state: SubscriptionState): string {
  switch (state) {
    case SubscriptionState.Active:
    case SubscriptionState.Trialing:
      return 'bg-green-100 text-green-700 border-green-300';
    case SubscriptionState.PastDue:
    case SubscriptionState.Incomplete:
      return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    case SubscriptionState.PendingCancellation:
      return 'bg-orange-100 text-orange-700 border-orange-300';
    case SubscriptionState.Canceled:
    case SubscriptionState.IncompleteExpired:
      return 'bg-red-100 text-red-700 border-red-300';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-300';
  }
}

// ── Icon helpers ──────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg aria-hidden className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const WarnIcon = () => (
  <svg aria-hidden className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
  </svg>
);

const ErrorIcon = () => (
  <svg aria-hidden className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const InfoIcon = () => (
  <svg aria-hidden className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ── Main component ────────────────────────────────────────────────────────

const ArtistBillingSettings: React.FC<ArtistBillingSettingsProps> = ({ artistId }) => {
  const [billing, setBilling] = useState<BillingAccount | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // per-action loading key: 'start' | 'resume' | 'refresh' | 'mode' | 'cancel'
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Start onboarding inline form — auto-open when onboarding has not started
  const [showStartForm, setShowStartForm] = useState(false);
  const [onboardingEmail, setOnboardingEmail] = useState('');

  // Subscribe form — shown in the SubscriptionState.None block
  const [subscribeEmail, setSubscribeEmail] = useState('');

  // ?subscribed=true query param — show a one-time success banner on return from Stripe
  const location = useLocation();
  const justSubscribed = new URLSearchParams(location.search).get('subscribed') === 'true';

  // Subscribe modal — auto-opens when subscription needs attention
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const hasAutoOpenedModal = useRef(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [account, planData] = await Promise.all([
          ApiService.getArtistBillingAccount(artistId),
          ApiService.getSubscriptionPlan(artistId).catch(() => null),
        ]);
        setBilling(account);
        setPlan(planData);
      } catch {
        setError('Failed to load billing settings. Try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [artistId]);

  const clearAction = () => {
    setActionError(null);
    setActionSuccess(null);
  };

  // Auto-open subscribe modal once when subscription needs attention
  useEffect(() => {
    if (!billing || hasAutoOpenedModal.current || justSubscribed) return;
    const needsSubscription =
      billing.monetizationMode === MonetizationMode.Subscription &&
      billing.onboardingState === OnboardingState.Complete &&
      [
        SubscriptionState.None,
        SubscriptionState.Incomplete,
        SubscriptionState.IncompleteExpired,
        SubscriptionState.Canceled,
        SubscriptionState.PastDue,
      ].includes(billing.subscriptionState);
    if (needsSubscription) {
      hasAutoOpenedModal.current = true;
      setShowSubscribeModal(true);
    }
  }, [billing, justSubscribed]);

  // ── Action handlers ───────────────────────────────────────────────────

  const handleStartOnboarding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onboardingEmail.trim()) return;
    clearAction();
    setActionLoading('start');
    try {
      const req: StartOnboardingRequest = { email: onboardingEmail.trim() };
      const link = await ApiService.startArtistOnboarding(artistId, req);
      window.location.href = link.url;
    } catch {
      setActionError('Unable to start Stripe setup. Please try again.');
      setActionLoading(null);
    }
  };

  const handleResumeOnboarding = async () => {
    clearAction();
    setActionLoading('resume');
    try {
      const link = await ApiService.resumeArtistOnboarding(artistId);
      window.location.href = link.url;
    } catch {
      setActionError('Unable to resume Stripe setup. Please try again.');
      setActionLoading(null);
    }
  };

  const handleRefreshOnboarding = async () => {
    clearAction();
    setActionLoading('refresh');
    try {
      const updated = await ApiService.refreshArtistOnboardingState(artistId);
      if (updated) setBilling(updated);
      setActionSuccess('Onboarding status updated.');
    } catch {
      setActionError('Unable to refresh status. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleModeSwitch = async (mode: MonetizationMode) => {
    if (!billing || mode === billing.monetizationMode) return;
    clearAction();
    setActionLoading('mode');
    try {
      const req: SetMonetizationModeRequest = { mode, email: '' };
      const updated = await ApiService.setArtistMonetizationMode(artistId, req);
      if (updated) setBilling(updated);
      setActionSuccess(
        `Switched to ${mode === MonetizationMode.Commission ? 'Commission' : 'Subscription'} mode.`
      );
    } catch {
      setActionError('Unable to change pricing mode. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (
      !window.confirm(
        'Cancel your subscription? You will keep access until the end of your current billing period.'
      )
    ) {
      return;
    }
    clearAction();
    setActionLoading('cancel');
    try {
      const updated = await ApiService.cancelArtistSubscription(artistId);
      if (updated) setBilling(updated);
      setActionSuccess('Subscription scheduled to cancel at the end of your billing period.');
    } catch {
      setActionError('Unable to cancel subscription. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subscribeEmail.trim()) return;
    clearAction();
    setActionLoading('subscribe');
    try {
      const checkoutUrl = await ApiService.createSubscriptionCheckout(artistId, subscribeEmail.trim());
      window.location.href = checkoutUrl;
    } catch {
      setActionError('Unable to start subscription checkout. Please try again.');
      setActionLoading(null);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        className="max-w-2xl mx-auto px-4 py-8 space-y-6"
        aria-busy="true"
        aria-label="Loading billing settings"
      >
        <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-4 w-72 bg-gray-100 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="h-28 bg-gray-100 rounded-xl animate-pulse" />
          <div className="h-28 bg-gray-100 rounded-xl animate-pulse" />
        </div>
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  // ── Error / empty state ───────────────────────────────────────────────

  if (error || !billing) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div
          role="alert"
          className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700"
        >
          <p className="font-semibold">Could not load billing settings</p>
          <p className="mt-1">{error ?? 'Unknown error. Please try refreshing the page.'}</p>
        </div>
      </div>
    );
  }

  const isActionBusy = actionLoading !== null;
  const isCommission = billing.monetizationMode === MonetizationMode.Commission;
  const isSubscription = billing.monetizationMode === MonetizationMode.Subscription;
  const onboardingNotStarted = billing.onboardingState === OnboardingState.NotStarted;

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Billing Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage how you get paid and your Stripe payout account.
        </p>
      </div>

      {/* Onboarding required banner */}
      {onboardingNotStarted && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-4">
          <WarnIcon />
          <div>
            <p className="text-sm font-semibold text-amber-900">Set up your payout account first</p>
            <p className="mt-0.5 text-sm text-amber-800">
              You must connect a Stripe account to receive payouts before you can choose a pricing mode.
              Complete the setup below to unlock all billing options.
            </p>
          </div>
        </div>
      )}

      {/* Subscription success banner — shown on return from Stripe Checkout */}
      {justSubscribed && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-start gap-3 rounded-xl border border-green-300 bg-green-50 px-4 py-4"
        >
          <CheckIcon />
          <div>
            <p className="text-sm font-semibold text-green-900">You're subscribed!</p>
            <p className="mt-0.5 text-sm text-green-800">
              Your InkVoyd subscription is being activated. It may take a moment to reflect below.
            </p>
          </div>
        </div>
      )}

      {/* Global action feedback */}
      {actionSuccess && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-700 flex items-center justify-between"
        >
          <span>{actionSuccess}</span>
          <button
            onClick={() => setActionSuccess(null)}
            className="ml-4 text-green-600 hover:text-green-800 text-xs underline"
            aria-label="Dismiss success message"
          >
            Dismiss
          </button>
        </div>
      )}
      {actionError && (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between"
        >
          <span>{actionError}</span>
          <button
            onClick={() => setActionError(null)}
            className="ml-4 text-red-600 hover:text-red-800 text-xs underline"
            aria-label="Dismiss error message"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Section 1: Pricing Mode — hidden until Stripe onboarding is started ── */}
      {!onboardingNotStarted && <section aria-labelledby="pricing-mode-heading">
        <h2 id="pricing-mode-heading" className="text-base font-semibold text-gray-900">
          Pricing Mode
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose how InkVoyd charges for your bookings. You can change this at any time.
        </p>

        <div
          className="mt-4 grid grid-cols-1 gap-4"
          role="radiogroup"
          aria-labelledby="pricing-mode-heading"
        >
          {/* Commission card — hidden until ready */}

          {/* Subscription card */}
          <div
            className="text-left rounded-xl border-2 border-indigo-500 bg-indigo-50 p-4 transition-all"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">Subscription</span>
              <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
                Current
              </span>
            </div>
            {plan ? (
              <>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: plan.currency }).format(plan.unitAmount / 100)}
                  <span className="text-sm font-normal text-gray-500"> / {plan.interval}</span>
                </p>
                {plan.features.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-1.5 text-sm text-gray-600">
                        <CheckIcon />
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                Pay a flat monthly fee. Keep 100% of your earnings.
              </p>
            )}
          </div>
        </div>
      </section>}

      {/* ── Section 2: Stripe Connect Onboarding ──────────────────────── */}
      <section aria-labelledby="onboarding-heading">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 id="onboarding-heading" className="text-base font-semibold text-gray-900">
            Payout Account
          </h2>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${onboardingBadgeClass(billing.onboardingState)}`}
          >
            {onboardingStateLabel(billing.onboardingState)}
          </span>
        </div>

        <div className="mt-3 rounded-xl border border-gray-200 bg-white overflow-hidden">

          {billing.onboardingState === OnboardingState.NotStarted ? (
            /* NotStarted: skip capability rows, show only the signup form */
            <div className="px-4 py-5 space-y-4">
              <p className="text-sm text-gray-600">
                Enter your email to create a Stripe Express account. You’ll be redirected to
                Stripe to add your bank details and identity information.
              </p>
              <form onSubmit={handleStartOnboarding} className="space-y-3">
                <div>
                  <label
                    htmlFor="onboarding-email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email for your Stripe account
                  </label>
                  <input
                    id="onboarding-email"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    placeholder="you@example.com"
                    value={onboardingEmail}
                    onChange={(e) => setOnboardingEmail(e.target.value)}
                    disabled={actionLoading === 'start'}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                  />
                </div>
                <button
                  type="submit"
                  disabled={actionLoading === 'start' || !onboardingEmail.trim()}
                  className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {actionLoading === 'start' ? 'Redirecting to Stripe…' : 'Set up payout account →'}
                </button>
              </form>
            </div>
          ) : (
            /* All other states: show capability rows + state-specific actions */
            <>
              <dl className="divide-y divide-gray-100">
                <div className="flex items-center justify-between px-4 py-3">
                  <dt className="text-sm text-gray-600">Charges</dt>
                  <dd>
                    {billing.chargesEnabled ? (
                      <span className="inline-flex items-center gap-1 text-sm text-green-700 font-medium">
                        <CheckIcon />
                        Enabled
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">Not enabled</span>
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <dt className="text-sm text-gray-600">Payouts</dt>
                  <dd>
                    {billing.payoutsEnabled ? (
                      <span className="inline-flex items-center gap-1 text-sm text-green-700 font-medium">
                        <CheckIcon />
                        Enabled
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">Not enabled</span>
                    )}
                  </dd>
                </div>
              </dl>

              <div className="border-t border-gray-100 px-4 py-4 space-y-3">

            {/* Pending */}
            {billing.onboardingState === OnboardingState.Pending && (
              <>
                <p className="text-sm text-gray-600">
                  Your Stripe setup is in progress. Finish it to enable charges and payouts.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleResumeOnboarding}
                    disabled={isActionBusy}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {actionLoading === 'resume' ? 'Redirecting…' : 'Continue setup'}
                  </button>
                  <button
                    onClick={handleRefreshOnboarding}
                    disabled={isActionBusy}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {actionLoading === 'refresh' ? 'Checking…' : 'Refresh status'}
                  </button>
                </div>
              </>
            )}

            {/* ChargesOnly */}
            {billing.onboardingState === OnboardingState.ChargesOnly && (
              <>
                <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-yellow-800">
                  <WarnIcon />
                  <p className="text-sm">
                    Charges are enabled but payouts are not yet active. Stripe may need
                    additional information from you.
                  </p>
                </div>
                <button
                  onClick={handleRefreshOnboarding}
                  disabled={isActionBusy}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {actionLoading === 'refresh' ? 'Checking…' : 'Refresh status'}
                </button>
              </>
            )}

            {/* Complete */}
            {billing.onboardingState === OnboardingState.Complete && (
              <p className="text-sm text-green-700 font-medium">
                Your Stripe account is fully set up. Charges and payouts are both enabled.
              </p>
            )}

            {/* Restricted */}
            {billing.onboardingState === OnboardingState.Restricted && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
                <ErrorIcon />
                <div>
                  <p className="text-sm font-medium">Account restricted by Stripe</p>
                  <p className="mt-0.5 text-xs text-red-700">
                    Sign in to your Stripe Dashboard to review and resolve any outstanding
                    requirements.
                  </p>
                </div>
              </div>
            )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Section 3: Subscription Status — hidden until Stripe onboarding is started ── */}
      {!onboardingNotStarted && isSubscription && (
        <section aria-labelledby="subscription-heading">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 id="subscription-heading" className="text-base font-semibold text-gray-900">
              Subscription
            </h2>
            {billing.subscriptionState !== SubscriptionState.None && (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${subscriptionBadgeClass(billing.subscriptionState)}`}
              >
                {subscriptionStateLabel(billing.subscriptionState)}
              </span>
            )}
          </div>

          <div className="mt-3 rounded-xl border border-gray-200 bg-white px-4 py-4 space-y-3">

            {/* None */}
            {billing.subscriptionState === SubscriptionState.None && (
              <>
                {billing.onboardingState !== OnboardingState.Complete ? (
                  /* Onboarding not finished — can't subscribe yet */
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
                    <WarnIcon />
                    <p className="text-sm">
                      Complete your Stripe payout setup above before subscribing.
                    </p>
                  </div>
                ) : (
                  /* Onboarding complete — show subscribe form */
                  <>
                    <p className="text-sm text-gray-600">
                      Subscribe to unlock flat-rate pricing. You'll be redirected to Stripe to complete payment.
                    </p>
                    <form onSubmit={handleSubscribe} className="space-y-3">
                      <div>
                        <label htmlFor="subscribe-email" className="block text-sm font-medium text-gray-700">
                          Email for your billing receipt
                        </label>
                        <input
                          id="subscribe-email"
                          type="email"
                          required
                          autoComplete="email"
                          placeholder="you@example.com"
                          value={subscribeEmail}
                          onChange={(e) => setSubscribeEmail(e.target.value)}
                          disabled={actionLoading === 'subscribe'}
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={actionLoading === 'subscribe' || !subscribeEmail.trim()}
                        className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {actionLoading === 'subscribe' ? 'Redirecting to Stripe…' : 'Subscribe →'}
                      </button>
                    </form>
                  </>
                )}
              </>
            )}

            {/* Active */}
            {billing.subscriptionState === SubscriptionState.Active && (
              <>
                <p className="text-sm text-gray-700">
                  Your subscription is active. You are on flat-rate billing with no
                  per-booking commission fee.
                </p>
                <button
                  onClick={handleCancelSubscription}
                  disabled={isActionBusy}
                  className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {actionLoading === 'cancel' ? 'Canceling…' : 'Cancel subscription'}
                </button>
              </>
            )}

            {/* Trialing */}
            {billing.subscriptionState === SubscriptionState.Trialing && (
              <>
                <p className="text-sm text-gray-700">
                  You are in your free trial period. Your subscription will activate
                  automatically when the trial ends.
                </p>
                <button
                  onClick={handleCancelSubscription}
                  disabled={isActionBusy}
                  className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {actionLoading === 'cancel' ? 'Canceling…' : 'Cancel subscription'}
                </button>
              </>
            )}

            {/* PastDue */}
            {billing.subscriptionState === SubscriptionState.PastDue && (
              <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-yellow-800">
                <WarnIcon />
                <div>
                  <p className="text-sm font-medium">Payment past due</p>
                  <p className="mt-0.5 text-xs text-yellow-700">
                    A recent payment failed. Update your payment method in your Stripe
                    Dashboard to keep your subscription active.
                  </p>
                </div>
              </div>
            )}

            {/* Incomplete */}
            {billing.subscriptionState === SubscriptionState.Incomplete && (
              <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-yellow-800">
                <WarnIcon />
                <div>
                  <p className="text-sm font-medium">Payment incomplete</p>
                  <p className="mt-0.5 text-xs text-yellow-700">
                    Your first payment has not yet succeeded. Complete payment in your Stripe
                    Dashboard to activate your subscription.
                  </p>
                </div>
              </div>
            )}

            {/* Canceled / IncompleteExpired */}
            {(billing.subscriptionState === SubscriptionState.Canceled ||
              billing.subscriptionState === SubscriptionState.IncompleteExpired) && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
                <ErrorIcon />
                <div>
                  <p className="text-sm font-medium">
                    {billing.subscriptionState === SubscriptionState.Canceled
                      ? 'Subscription canceled'
                      : 'Subscription expired'}
                  </p>
                  <p className="mt-0.5 text-xs text-red-700">
                    {billing.subscriptionState === SubscriptionState.Canceled
                      ? 'Your subscription has ended. Contact support to start a new one.'
                      : 'Your incomplete subscription expired. Contact support to subscribe again.'}
                  </p>
                </div>
              </div>
            )}

            {/* PendingCancellation */}
            {billing.subscriptionState === SubscriptionState.PendingCancellation && (
              <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-orange-800">
                <InfoIcon />
                <p className="text-sm">
                  Your subscription is active but will not renew. You will retain access until
                  the end of your current billing period.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Subscribe modal ───────────────────────────────────────────── */}
      {showSubscribeModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="subscribe-modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSubscribeModal(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 space-y-5">
            {/* Close button */}
            <button
              onClick={() => setShowSubscribeModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 rounded"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100">
                <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h2 id="subscribe-modal-title" className="text-base font-semibold text-gray-900">
                  Activate your subscription
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {billing?.subscriptionState === SubscriptionState.PastDue
                    ? 'Your subscription payment is past due. Update your payment to keep access.'
                    : billing?.subscriptionState === SubscriptionState.Canceled ||
                      billing?.subscriptionState === SubscriptionState.IncompleteExpired
                    ? 'Your subscription has ended. Re-subscribe to restore flat-rate access.'
                    : 'You\'re on Subscription mode but don\'t have an active plan yet. Subscribe to unlock full access.'}
                </p>
              </div>
            </div>

            {/* Plan pill */}
            {plan && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 flex items-center justify-between">
                <span className="text-sm font-medium text-indigo-900">{plan.productName}</span>
                <span className="text-sm font-semibold text-indigo-700">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: plan.currency }).format(plan.unitAmount / 100)}
                  <span className="font-normal text-indigo-500"> / {plan.interval}</span>
                </span>
              </div>
            )}

            {/* Subscribe form */}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await handleSubscribe(e);
                setShowSubscribeModal(false);
              }}
              className="space-y-3"
            >
              <div>
                <label htmlFor="modal-subscribe-email" className="block text-sm font-medium text-gray-700">
                  Email for your billing receipt
                </label>
                <input
                  id="modal-subscribe-email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  placeholder="you@example.com"
                  value={subscribeEmail}
                  onChange={(e) => setSubscribeEmail(e.target.value)}
                  disabled={actionLoading === 'subscribe'}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                />
              </div>
              <button
                type="submit"
                disabled={actionLoading === 'subscribe' || !subscribeEmail.trim()}
                className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {actionLoading === 'subscribe' ? 'Redirecting to Stripe…' : 'Subscribe now →'}
              </button>
            </form>

            <button
              type="button"
              onClick={() => setShowSubscribeModal(false)}
              className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              I'll do this later
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistBillingSettings;
